import React from "react"
import { Box } from "@mui/material"
import { useSelector } from "react-redux"
import { AppState } from "store/store"
import { EasyCalcResIf } from "utils/ifs/EasyCalcRes"
import { loadResponseEasyCalc } from "utils/JsonManipulation"
import {
  CAR_TYPE_FROM_API,
  DRIVER_SCOPE_FROM_API,
  LICENSE_COLOR_FROM_API,
  PURPOSE_FROM_API,
  HAD_ACCIDENT,
  APP_URL
} from "Const"
import { prefectures } from "utils/ConditionData"
import { DriverSelectIf } from "store/states/UserInfoState"
import GoBackButton from "components/Button/GoBackButton"
import styles from "./ModalDefault.module.scss"

const dot = "・"
const colon = ": "
interface RowProps {
  itemNm: string
  itemValue: string
  extraInfo?: string
}
const Row = ({ itemNm, itemValue, extraInfo = "" }: RowProps) => {
  return (
    <li key={itemNm}>
      <div>{dot}</div>
      <div>{itemNm}</div>
      <div>{colon}</div>
      <div>
        {itemValue}
        {extraInfo !== "" && <div className={styles.fontSmall}>{extraInfo}</div>}
      </div>
    </li>
  )
}
interface RowForDriverSelectProps {
  list: Array<string>
  SC: string | JSX.Element
}
const RowForDriverSelect = ({ list, SC }: RowForDriverSelectProps) => {
  return (
    <li className={styles.condList__driverSelect}>
      <div>{dot}</div>
      <div>補償範囲</div>
      <div>{colon}</div>
      <ul>
        {list.map((x) => {
          return <li key={x}>{x}</li>
        })}
        {SC !== "" && <div className={styles.fontSmall}>({SC})</div>}
      </ul>
    </li>
  )
}
interface DriverScopeIf {
  "IT-A自動車-特定特約コード": string
}
/**
 * 見積もり条件モーダル
 */
const ModalCondList = () => {
  const [isAEBVisible, setIsAEBVisible] = React.useState(false)
  const [isDistanceVisible, setIsDistanceVisible] = React.useState(false)
  const [isAccidentExpVisible, setIsAccidentExpVisible] = React.useState(false)
  // 車情報
  const {
    vehicleType,
    carModel,
    firstInspectionYear,
    firstInspectionMonth,
    carName,
    automaticBraking,
    isAEBShown,
    aebButtonSelected
  } = useSelector((state: AppState) => state.qr)
  // ユーザー情報
  const contractorAge = useSelector((state: AppState) => state.userInfo.contractorAge)
  const youngestAge = useSelector((state: AppState) => state.userInfo.youngestAge)
  const licenceColor = useSelector((state: AppState) => state.userInfo.licenceColor)
  const purpose = useSelector((state: AppState) => state.userInfo.purpose)
  const distance = useSelector((state: AppState) => state.userInfo.distance)
  const locationName = useSelector((state: AppState) => state.userInfo.location)
  const hadAccidentSelect = useSelector((state: AppState) => state.userInfo.hadAccidentSelect)
  const nonFleetGrade = useSelector((state: AppState) => state.userInfo.nonFleetGrade)
  const driverScope: Array<DriverScopeIf> = useSelector((state: AppState) => state.userInfo.driverScope)
  const driverSelect: DriverSelectIf = useSelector((state: AppState) => state.userInfo.driverSelect)
  // 見積情報
  const res: EasyCalcResIf = useSelector((state: AppState) => state.plan.plan)
  const responseData = loadResponseEasyCalc(res)
  // 表示・非表示チェック
  React.useEffect(() => {
    const isNewToInsurance = nonFleetGrade === "06S" || nonFleetGrade === "07S"
    setIsAEBVisible(isAEBShown)
    // 保険未加入 → 事故有無は非表示
    setIsAccidentExpVisible(!isNewToInsurance)
    // 保険未加入 → 前年走行距離は非表示
    setIsDistanceVisible(!isNewToInsurance)
  }, [isAEBShown, purpose, nonFleetGrade])
  const getCarTypeNm = () => {
    if (vehicleType === "11") {
      return CAR_TYPE_FROM_API[11]
    }
    if (vehicleType === "13") {
      return CAR_TYPE_FROM_API[13]
    }
    if (vehicleType === "31") {
      return CAR_TYPE_FROM_API[31]
    }
    if (vehicleType === "33") {
      return CAR_TYPE_FROM_API[33]
    }
    if (vehicleType === "49") {
      return CAR_TYPE_FROM_API[49]
    }
    return ""
  }
  const getAEBInfo = () => {
    const AEBInfo = {
      value: "",
      discount: ""
    }
    if (aebButtonSelected === "no") {
      AEBInfo.value = "なし"
      AEBInfo.discount = "(自動ブレーキ割引なし)"
    } else if (aebButtonSelected === "yes") {
      AEBInfo.value = "あり"
      AEBInfo.discount = "(自動ブレーキ割引あり)"
    } else if (aebButtonSelected === "unKnown") {
      AEBInfo.value = "わからない"
      AEBInfo.discount = "(自動ブレーキ割引なし)"
    }
    return AEBInfo
  }
  const getLicenseColor = () => {
    if (licenceColor === "0") {
      return LICENSE_COLOR_FROM_API[0]
    }
    if (licenceColor === "1") {
      return LICENSE_COLOR_FROM_API[1]
    }
    if (licenceColor === "2") {
      return LICENSE_COLOR_FROM_API[2]
    }
    return ""
  }
  const getPurpose = () => {
    if (purpose === "1") {
      return PURPOSE_FROM_API[1]
    }
    if (purpose === "2") {
      return PURPOSE_FROM_API[2]
    }
    if (purpose === "3") {
      return PURPOSE_FROM_API[3]
    }
    return ""
  }
  const getDriverScope = () => {
    const value = driverScope.map((x) => {
      if (x["IT-A自動車-特定特約コード"] === "Eﾃ") {
        return DRIVER_SCOPE_FROM_API.Eﾃ
      }
      if (x["IT-A自動車-特定特約コード"] === "EB") {
        if (purpose === "3") {
          return (
            <>
              {DRIVER_SCOPE_FROM_API.EB_BUSINESS}
              <br />
              お子さま（同居）または使用人の方の年齢 {youngestAge}
              {youngestAge === "30" ? "歳以上" : "歳"}
            </>
          )
        }
        return (
          <>
            {DRIVER_SCOPE_FROM_API.EB}
            <br />
            お子さま(同居)の年齢 {youngestAge}
            {youngestAge === "30" ? "歳以上" : "歳"}
          </>
        )
      }
      if (x["IT-A自動車-特定特約コード"] === "Eﾔ") {
        return DRIVER_SCOPE_FROM_API.Eﾔ
      }
      if (x["IT-A自動車-特定特約コード"] === "11") {
        if (purpose === "3") {
          return DRIVER_SCOPE_FROM_API["11_BUSINESS"]
        }
        return DRIVER_SCOPE_FROM_API[11]
      }
      return ""
    })
    return value[0]
  }
  const getDriverSelect = () => {
    const list: Array<string> = ["主にお車を使用される方"]
    if (driverSelect.spouse) {
      list.push("配偶者")
    }
    if (driverSelect.dependents) {
      list.push("お子さま（同居）その方の配偶者含む")
    }
    if (driverSelect.unmarried) {
      list.push("お子さま（別居・未婚）")
    }
    if (driverSelect.married) {
      list.push("お子さま（別居・既婚）")
    }
    if (driverSelect.consent) {
      list.push("同居の親族")
    }
    if (driverSelect.friend) {
      list.push("別居の親族・友人・知人")
    }
    if (driverSelect.servant) {
      list.push("使用人の方")
    }
    return list
  }
  const getAccidentInThePast = () => {
    const accident = {
      hadAccidents: "",
      extraInfo: ""
    }
    if (hadAccidentSelect === HAD_ACCIDENT.NO_ACCIDENT) {
      accident.hadAccidents = HAD_ACCIDENT.NO_ACCIDENT
      accident.extraInfo = "(事故有係数適用期間なし)"
    }
    if (hadAccidentSelect === HAD_ACCIDENT.PAST_2_3_YEARS) {
      accident.hadAccidents = HAD_ACCIDENT.PAST_2_3_YEARS
      accident.extraInfo = "(事故有係数適用期間あり)"
    }
    if (hadAccidentSelect === HAD_ACCIDENT.PAST_1_YEAR) {
      accident.hadAccidents = HAD_ACCIDENT.PAST_1_YEAR
      accident.extraInfo = "(事故有係数適用期間あり)"
    }
    return accident
  }
  const getDistance = () => {
    if (parseInt(distance, 10) <= 3000) {
      return "3,000km以下"
    }
    if (parseInt(distance, 10) <= 5000) {
      return "5,000km以下"
    }
    if (parseInt(distance, 10) > 5000 && parseInt(distance, 10) <= 7000) {
      return "7,000km以下"
    }
    if (parseInt(distance, 10) > 7000 && parseInt(distance, 10) <= 10000) {
      return "10,000km以下"
    }
    if (parseInt(distance, 10) > 10000 && parseInt(distance, 10) <= 15000) {
      return "15,000km以下"
    }
    if (parseInt(distance, 10) > 15000 && parseInt(distance, 10) <= 20000) {
      return "20,000km以下"
    }
    return "20,000km超"
  }
  const getNonFleetGrade = () => {
    if (nonFleetGrade === "06S" || nonFleetGrade === "07S" || nonFleetGrade === "06F" || nonFleetGrade === "07F") {
      return nonFleetGrade.slice(-2)
    }
    return parseInt(nonFleetGrade, 10).toString()
  }
  return (
    <>
      <Box className={styles.modalTitle}>
        <h3>お見積りの条件</h3>
      </Box>
      <Box className={`${styles.modalParagraph} ${styles.condList}`}>
        <ul>
          <Row itemNm="用途車種" itemValue={getCarTypeNm()} />
          <Row itemNm="型式" itemValue={carModel} />
          <Row itemNm="初度登録（検査）年月" itemValue={`${firstInspectionYear}年${firstInspectionMonth}月`} />
          <Row itemNm="車名" itemValue={carName} />
          {isAEBVisible && (
            <Row itemNm="自動ブレーキ" itemValue={getAEBInfo().value} extraInfo={getAEBInfo().discount} />
          )}
          <Row itemNm="ご年齢" itemValue={`${contractorAge}歳`} />
          <Row itemNm="免許証の色" itemValue={getLicenseColor()} />
          <Row itemNm="主な使用目的" itemValue={getPurpose()} />
          <Row itemNm="主な使用地域" itemValue={prefectures[parseInt(locationName, 10) - 1]} />
          <RowForDriverSelect list={getDriverSelect()} SC={getDriverScope()} />
          <Row itemNm="ノンフリート等級" itemValue={getNonFleetGrade()} />
          {isAccidentExpVisible && (
            <Row
              itemNm="保険金を請求したお車の事故"
              itemValue={getAccidentInThePast().hadAccidents}
              extraInfo={getAccidentInThePast().extraInfo}
            />
          )}
          {isDistanceVisible && <Row itemNm="前年走行距離" itemValue={getDistance()} />}
        </ul>
        <div className={styles.condList__goBackButton}>
          <GoBackButton where={APP_URL.CONDITIONS} text="お見積り条件を修正する" />
        </div>
      </Box>
    </>
  )
}
export default ModalCondList
